import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Check } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";

export default function SpecimenTerms() {
  const [d, setD] = useState(null);

  useSeo({
    title: "Specimen Heads Of Terms: Land Into A Facility — KickStartKenyaFootball",
    description:
      "A published specimen showing the shape of a lease or revenue-share arrangement that turns idle land into a football facility. Not legal advice, not drafted for your land, and to be taken to your own advocate.",
  });

  useEffect(() => {
    api.get("/specimen-heads-of-terms").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);

  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="specimen-loading" />;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="specimen-page">
      <div className="mx-auto max-w-[900px] px-5 md:px-10">
        <p className="overline text-ochre">{d.eyebrow}</p>
        <h1 className="display-1 mt-5">{d.heading}</h1>

        <div className="mt-10 border-l-2 border-maasai bg-[#0b0b0b] p-6 md:p-7" data-testid="specimen-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} className="mt-1 shrink-0 text-maasai" />
            <p className="text-sm leading-relaxed text-zinc-300">{d.intro}</p>
          </div>
        </div>

        <div className="mt-12 grid gap-px bg-zinc-800" data-testid="specimen-sections">
          {d.sections.map(([title, body], i) => (
            <Reveal key={title} delay={Math.min(i * 0.04, 0.2)}>
              <div className="bg-[#0b0b0b] p-6 md:p-8" data-testid={`specimen-section-${i}`}>
                <div className="flex items-baseline gap-4">
                  <span className="mono text-[0.6rem] text-ochre">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-display text-base uppercase tracking-tight text-white md:text-lg">{title}</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-zinc-300">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <section className="mt-16" data-testid="specimen-before-you-sign">
          <p className="eyebrow">Before anybody signs anything</p>
          <h2 className="display-3 mt-4">Four Things, Every Time</h2>
          <ul className="mt-8 space-y-4">
            {d.before_you_sign.map((t, i) => (
              <li key={i} className="flex gap-3 text-sm leading-relaxed text-zinc-300" data-testid={`specimen-rule-${i}`}>
                <Check size={15} className="mt-1 shrink-0 text-ochre" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-16 border-l-2 border-maasai bg-[#0b0b0b] p-7" data-testid="specimen-regulatory">
          <p className="eyebrow">{d.regulatory.heading}</p>
          <ul className="mt-5 space-y-3">
            {d.regulatory.today.map((t, i) => (
              <li key={i} className="flex gap-3 text-sm leading-relaxed text-zinc-300">
                <Check size={14} className="mt-1 shrink-0 text-ochre" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 border-t border-white/10 pt-5 text-xs leading-relaxed text-zinc-400">
            {d.regulatory.later}
          </p>
        </section>

        <div className="mono mt-12 flex flex-wrap gap-x-8 gap-y-3 text-[0.6rem] uppercase tracking-[0.16em]">
          <Link to="/projects" className="text-ochre hover:text-white" data-testid="specimen-to-projects">
            County projects
          </Link>
          <Link to="/professionals" className="text-ochre hover:text-white" data-testid="specimen-to-professionals">
            Kenyan professionals you can appoint yourself
          </Link>
          <Link to="/idle-acre" className="text-ochre hover:text-white" data-testid="specimen-to-idle-acre">
            What your idle acre is worth
          </Link>
        </div>
      </div>
    </div>
  );
}
